import {
    Component,
    Emitter,
    MeshComponent,
    Object3D,
    TextComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {PhongMaterial, TextMaterial} from '../types/materials.js';
import {Easing, clamp, lerp} from '../utils/lerp.js';

/**
 * text-fade / object-fade
 * Start Fading by setting lerpTime to 0
 */
export class TextFade extends Component {
    static TypeName = 'text-fade';

    @property.object()
    textToFade?: Object3D;

    @property.float(1)
    fadeSpeed = 1;

    @property.string('text')
    component = 'text';

    private _textObj?: Object3D;
    private originalColor = new Float32Array(4);
    private alphaColor = new Float32Array(4);
    private componentToFade?: MeshComponent | TextComponent;
    private materialToFade?: PhongMaterial | TextMaterial;
    private deltaTime = 0;
    private isFadingIn = false;
    private isFadingOut = false;
    private materialPipeline = '';

    /**
     * Event emitter for when the object is done fading in
     */
    public fadedIn = new Emitter();

    /**
     * Event emitter for when the object is done fading out
     */
    public fadedOut = new Emitter();

    get textObj() {
        return this._textObj;
    }

    fadeIn() {
        if (!this.isFadingIn && !this.isFadingOut) {
            this.deltaTime = 0;
        }
        this.object.active = true;
        this.isFadingIn = true;
        this.isFadingOut = false;
    }

    fadeOut() {
        if (!this.isFadingIn && !this.isFadingOut) {
            this.deltaTime = 0;
        }
        this.isFadingIn = false;
        this.isFadingOut = true;
    }

    start() {
        /* Set this to Text Object that you want to Fade */
        this._textObj = this.textToFade || this.object;

        if (this.component === 'text') {
            const tempTextComponent = this._textObj.getComponent(TextComponent);
            if (!tempTextComponent) {
                throw new Error(`text-fade(${this.object.name}): TextComponent missing`);
            }
            this.componentToFade = tempTextComponent;

            const material = tempTextComponent.material as TextMaterial;
            tempTextComponent.material =
                material.clone(); /* Clone material, to only affect this object's material */
            this.originalColor.set(material.color);
            this.materialToFade = tempTextComponent.material as TextMaterial;
        } else if (this.component === 'mesh') {
            const tempMeshComponent = this._textObj.getComponent(MeshComponent);
            if (!tempMeshComponent) {
                throw new Error(`text-fade(${this.object.name}): MeshComponent missing`);
            }
            this.componentToFade = tempMeshComponent;
            const material = tempMeshComponent.material as PhongMaterial;
            tempMeshComponent.material =
                material.clone(); /* Clone material, to only affect this object's material */
            this.originalColor.set(material.diffuseColor);
            this.materialToFade = tempMeshComponent.material as PhongMaterial;
        } else {
            throw new Error(
                `text-fade(${this.object.name}): component is not text or mesh`
            );
        }

        //Turning it to Vec4 as Float32Array causes weird results with Lerp for some reason
        this.alphaColor.set(this.originalColor);

        this.materialPipeline = this.componentToFade.material!.pipeline;
    }

    update(dt: number) {
        if (!this.isFadingIn && !this.isFadingOut) {
            return;
        }

        this.deltaTime = clamp(this.deltaTime, 0, 1);

        let alpha = 0;
        this.deltaTime += dt / this.fadeSpeed;
        if (this.isFadingIn) {
            alpha = clamp(lerp(0, 1, this.deltaTime, Easing.InQuad), 0, 1);
        }
        if (this.isFadingOut) {
            alpha = clamp(lerp(1, 0, this.deltaTime, Easing.OutQuad), 0, 1);
        }

        this.alphaColor[3] = alpha;

        /* Apply lerp on text/mesh */
        if (this.materialPipeline === 'Text') {
            (this.materialToFade as TextMaterial).color = this.alphaColor;
        } else if (this.materialPipeline.startsWith('Phong')) {
            (this.materialToFade as PhongMaterial).diffuseColor = this.alphaColor;
        } else {
            throw new Error(
                `text-fade(${this.object.name}): materialToFade is missing color properties`
            );
        }
        if (this.deltaTime >= 1) {
            if (this.isFadingIn) {
                this.fadedIn.notify();
            }
            if (this.isFadingOut) {
                this.fadedOut.notify();
                //   this.object.active = false;
            }
            this.isFadingIn = false;
            this.isFadingOut = false;
        }
    }
}
